import { DataUtilities } from "../../DataUtilities";
import { DataDefinition } from "../DataDefinition";
import { NumberDataDefinition } from "./NumberDataDefinition";
import { DateDataDefinition } from "./DateDataDefinition";
import { StringDataDefinition } from "./StringDataDefinition";
import { BooleanDataDefinition } from "./BooleanDataDefinition";
import { BaseDataDefinition } from "./BaseDataDefinition";
import { IDataDefinition } from "./IDataDefinition";
import { FileDataDefinition } from "./FileDataDefinition";

import { IDataDefinitionOwner } from "../IDataDefinitionOwner";
import { DataTypes } from "../DataTypes";
import { DataDefinitionUtilities } from "../../DataDefinitionUtilities";
import { ValidationIssue } from "../../IDesignValidationProvider";
import type { AppGlueAny } from "../../AppGlueAny";

export class ObjectDataDefinition extends BaseDataDefinition<object> implements IDataDefinitionOwner {
    readonly type: DataTypes = DataTypes.OBJECT;

    keyField?: string;
    fields: IDataDefinition[] = [];

    // update fields only on applyFieldsPath, when it's empty, it means that apply all changes
    static parseObject(
        owner: IDataDefinitionOwner,
        checkVal: object,
        data: object,
        isList: boolean,
        name: string,
        currentDefinition: IDataDefinition,
        removeItemsNotInSchema: boolean,
        reorderElementsToMatch: boolean,
    ): IDataDefinition | null {
        // check if date
        if (DataUtilities.isValidDate(checkVal)) {
            let dateDef = (currentDefinition as DateDataDefinition) ?? new DateDataDefinition();

            dateDef.name = name;
            dateDef.owner = owner;
            dateDef.list = isList;

            // if (isList) {
            //     dateDef.setListValue(data as Date[]);
            // } else {
            //     dateDef.setValue(data as Date);
            // }

            return dateDef;
        }

        // check if file
        if (DataUtilities.isValidFile(checkVal)) {
            let dateDef = (currentDefinition as FileDataDefinition) ?? new FileDataDefinition();

            dateDef.name = name;
            dateDef.owner = owner;
            dateDef.list = isList;

            // update the value globally, insteadof one by one
            // if (isList) {
            //     dateDef.setListValue(data as FileData[]);
            // } else {
            //     dateDef.setValue(data as FileData);
            // }

            return dateDef;
        }

        // default parse as object

        let objectDef = (currentDefinition as ObjectDataDefinition) ?? new ObjectDataDefinition();
        objectDef.name = name;
        objectDef.owner = owner;

        objectDef.list = isList;

        // update the value globally, insteadof one by one
        // if (isList) {
        //     objectDef.setListValue(data as object[]);
        // } else {
        //     objectDef.setValue(data);
        // }

        objectDef.fields = this.parseFieldsForObject(
            objectDef,
            checkVal,
            objectDef.fields,
            removeItemsNotInSchema,
            reorderElementsToMatch,
        );

        return objectDef;
    }

    // applyFieldsPath

    // update fields only on applyFieldsPath, when it's empty, it means that apply all changes
    static parseFieldsForObject(
        owner: IDataDefinitionOwner,
        data: object,
        existingFields: IDataDefinition[],
        removeItemsNotInSchema: boolean,
        reorderElementsToMatch: boolean,
    ): IDataDefinition[] {
        let newFieldsMap: { [fieldName: string]: IDataDefinition } = {};
        let newFieldList: IDataDefinition[] = [];

        let oldFieldsMap: { [fieldName: string]: IDataDefinition } = {};

        for (let oField of existingFields) {
            if (oField.name) {
                oldFieldsMap[oField.name] = oField;
            }
        }

        for (let name in data) {
            let val = Reflect.get(data, name);
            let checkVal = val;

            let isArray = false;
            if (Array.isArray(val)) {
                isArray = true;
                checkVal = val[0];
            }

            let def: IDataDefinition | null = null;
            if (typeof checkVal === "string") {
                // see if it is a date string
                if (DataUtilities.isDateString(checkVal)) {
                    let sdef = (oldFieldsMap[name] as DateDataDefinition) ?? new DateDataDefinition();
                    sdef.name = name;
                    sdef.owner = owner;
                    // update the value globally, insteadof one by one
                    // if (isArray) {
                    //     //todo: handle date array
                    //     let dateArray: Date[] = [];

                    //     for (let dString of val) {
                    //         dateArray.push(DataUtilities.getDateFromString(dString));
                    //     }

                    //     sdef.setListValue(dateArray);
                    // } else {
                    //     sdef.setValue(DataUtilities.getDateFromString(val));
                    // }

                    sdef.list = isArray;

                    def = sdef;
                } else {
                    let sdef = (oldFieldsMap[name] as StringDataDefinition) ?? new StringDataDefinition();

                    sdef.name = name;
                    sdef.owner = owner;

                    // update the value globally, insteadof one by one
                    // if (isArray) {
                    //     sdef.setListValue(val);
                    // } else {
                    //     sdef.setValue(val);
                    // }

                    sdef.list = isArray;

                    def = sdef;
                }
            } else if (typeof checkVal === "number") {
                let sdef = (oldFieldsMap[name] as NumberDataDefinition) ?? new NumberDataDefinition();
                sdef.name = name;
                sdef.owner = owner;

                // if (isArray) {
                //     sdef.setListValue(val);
                // } else {
                //     sdef.setValue(val);
                // }

                sdef.list = isArray;

                def = sdef;
            } else if (typeof checkVal === "boolean") {
                let sdef = (oldFieldsMap[name] as BooleanDataDefinition) ?? new BooleanDataDefinition();
                sdef.name = name;
                sdef.owner = owner;

                // if (isArray) {
                //     sdef.setListValue(val);
                // } else {
                //     sdef.setValue(val);
                // }

                sdef.list = isArray;

                def = sdef;
            } else {
                def = ObjectDataDefinition.parseObject(
                    owner,
                    checkVal,
                    val,
                    isArray,
                    name,
                    oldFieldsMap[name],
                    removeItemsNotInSchema,
                    reorderElementsToMatch,
                );
            }

            if (def && def.name) {
                newFieldsMap[def.name] = def;
                newFieldList.push(def);
            }
        }
        return DataDefinition.reconcileFieldList(
            removeItemsNotInSchema,
            reorderElementsToMatch,
            newFieldList,
            newFieldsMap,
            existingFields,
        );
    }

    // applyFieldsPath

    static fromJSONObjectValue(value: AppGlueAny, name?: string): ObjectDataDefinition | undefined {
        if (Array.isArray(value)) {
            if (value.length === 0) {
                throw new Error("invalid json object value");
            }

            if (!value.find((v) => typeof v !== "object")) {
                let def = new ObjectDataDefinition(name);
                def.list = true;

                for (let key of Object.keys(value[0])) {
                    let val = Reflect.get(value[0], key);

                    let field: IDataDefinition | undefined;

                    field = DateDataDefinition.fromJSONObjectValue(val, key);
                    if (!field) field = StringDataDefinition.fromJSONObjectValue(val, key);

                    if (!field) field = NumberDataDefinition.fromJSONObjectValue(val, key);

                    if (!field) field = FileDataDefinition.fromJSONObjectValue(val, key);

                    if (!field) field = BooleanDataDefinition.fromJSONObjectValue(val, key);

                    if (!field) {
                        field = ObjectDataDefinition.fromJSONObjectValue(val, key) as ObjectDataDefinition | undefined;
                    }

                    if (!field) throw new Error(`[${key}] field is not valid`);

                    def.fields.push(field);
                }

                return def;
            }
        } else if (typeof value === "object") {
            let def = new ObjectDataDefinition(name);

            for (let key of Object.keys(value)) {
                let val = Reflect.get(value, key);

                let field: IDataDefinition | undefined;

                field = DateDataDefinition.fromJSONObjectValue(val, key);

                if (!field) field = StringDataDefinition.fromJSONObjectValue(val, key);

                if (!field) field = NumberDataDefinition.fromJSONObjectValue(val, key);

                if (!field) field = ObjectDataDefinition.fromJSONObjectValue(val, key);

                if (!field) field = FileDataDefinition.fromJSONObjectValue(val, key);

                if (!field) field = BooleanDataDefinition.fromJSONObjectValue(val, key);

                if (!field) throw new Error(`[${key}] field is not valid`);

                def.fields.push(field);
            }

            return def;
        }

        return undefined;
    }

    static fromJSONSchema(obj: object, name?: string): ObjectDataDefinition | undefined {
        let def = new ObjectDataDefinition(name);

        let type = Reflect.get(obj, "type");
        if (type === "array") {
            def.list = true;
            let items = Reflect.get(obj, "items");

            if (typeof items === "object") {
                for (let key of Object.keys(items)) {
                    let fieldSchema = Reflect.get(items, key);

                    let field: IDataDefinition | undefined;

                    field = DateDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) field = StringDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) field = NumberDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) field = ObjectDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) field = FileDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) field = BooleanDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) throw new Error(`[${key}] field is not valid`);

                    def.fields.push(field);
                }
                return def;
            }
        } else if (type === "object") {
            let items = Reflect.get(obj, "properties");
            if (typeof items === "object") {
                for (let key of Object.keys(items)) {
                    let fieldSchema = Reflect.get(items, key);

                    let field: IDataDefinition | undefined;

                    field = DateDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) field = StringDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) field = NumberDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) field = ObjectDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) field = FileDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) field = BooleanDataDefinition.fromJSONSchema(fieldSchema, key);

                    if (!field) throw new Error(`[${key}] field is not valid`);

                    def.fields.push(field);
                }
                return def;
            }
        }

        return undefined;
    }

    getValueObject(): { [propertyName: string]: AppGlueAny } {
        if (this.owner && this.name) {
            let valObj = this.owner.getValueObject()[this.name];

            if (!valObj) {
                valObj = {};
                this.owner.getValueObject()[this.name] = valObj;
            }

            // check array
            if (Array.isArray(valObj) !== this.list) {
                if (this.list) {
                    // set array
                    valObj = [];
                    this.owner.getValueObject()[this.name] = valObj;
                } else {
                    // set object
                    valObj = {};
                    this.owner.getValueObject()[this.name] = valObj;
                }
            }

            return valObj;
        }
        // this should not happen
        return this.list ? [] : {};
    }

    getValue(): object | undefined {
        return this.getValueObject();
    }

    setValue(value: object) {
        if (this.owner && this.name) {
            this.owner.getValueObject()[this.name] = value;
        }
    }

    getListValue(): object[] | undefined {
        return this.getValueObject() as object[];
    }

    setListValue(value: object[]) {
        if (this.owner && this.name) {
            this.owner.getValueObject()[this.name] = value;
        }
    }

    getStorageData(): object {
        let object: object = super.getStorageData();

        Reflect.set(object, "keyField", this.keyField);

        let fieldData: object[] = [];

        for (let f of this.fields) {
            fieldData.push(f.getStorageData());
        }

        Reflect.set(object, "fields", fieldData);
        return object;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        this.keyField = Reflect.get(data, "keyField");
        let fieldData = Reflect.get(data, "fields") as AppGlueAny[];

        for (let f of fieldData) {
            let val = DataDefinitionUtilities.getDataDefinitionFromData(f)!;
            this.fields.push(val);
        }
    }

    convert(value: AppGlueAny): AppGlueAny {
        // do not convert these
        return value;
    }

    verifyValueType(value: AppGlueAny): ValidationIssue | undefined {
        if (value === undefined) return new ValidationIssue("value is undefined");

        if (value === null) return new ValidationIssue("value is unset");

        if (typeof value !== "object") return new ValidationIssue("value is not an object");

        let issues: string[] = [];

        if (Array.isArray(value)) {
            for (let idx in value) {
                let v = value[idx];
                if (typeof v !== "object") {
                    issues.push(`${parseInt(idx, 10) + 1}th value is not object`);
                    continue;
                }

                issues = this.compareObjectToStructure(v, issues);
            }
        } else {
            issues = this.compareObjectToStructure(value, issues);
        }

        if (issues.length === 0) return undefined;

        return new ValidationIssue(issues.join(", "));
    }

    toJSONSchema(): object {
        if (this.list) {
            return {
                type: "array",
                items: this.fields.reduce((s, c: IDataDefinition) => {
                    Reflect.set(s, c.name, c.toJSONSchema());
                    return s;
                }, {}),
            };
        }
        return {
            type: this.type,
            properties: this.fields.reduce((s, c: IDataDefinition) => {
                Reflect.set(s, c.name, c.toJSONSchema());
                return s;
            }, {}),
        };
    }

    toJSONObject(value: AppGlueAny): object {
        if (this.list) {
            if (!Array.isArray(value)) {
                value = [value];
            }

            if (value.length === 0) {
                value = [null];
            }

            let result: AppGlueAny[] = [];

            for (let v of value) {
                if (typeof v !== "object") {
                    v = {};
                }
                result.push(
                    this.fields.reduce((s, c: IDataDefinition) => {
                        Reflect.set(s, c.name, c.toJSONObject(Reflect.get(v || {}, c.name)));
                        return s;
                    }, {}),
                );
            }

            return result;
        }

        if (Array.isArray(value)) {
            value = value.length ? value[0] : null;
        }

        // invalid object
        if (typeof value !== "object") {
            value = {};
        }

        return this.fields.reduce((s, c: IDataDefinition) => {
            Reflect.set(s, c.name, c.toJSONObject(Reflect.get(value || {}, c.name)));
            return s;
        }, {});
    }

    applyOwner(owner?: IDataDefinitionOwner) {
        this.owner = owner;

        if (owner) {
            this.fields.forEach((f) => {
                if (f instanceof ObjectDataDefinition) {
                    f.applyOwner(this);
                } else {
                    f.owner = this;
                }
            });
        }
    }

    toStructureString(indent?: number): string {
        return `${super.toStructureString(indent)}${this.fields
            .map((f) => f.toStructureString((indent || 0) + 4))
            .join("")}`;
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues = super.getDesignValidationIssues();

        for (let f of this.fields) {
            let fissues = f.getDesignValidationIssues();
            if (fissues && fissues.length !== 0) {
                issues.push(new ValidationIssue("Field(s) have validation issues"));
                break;
            }
        }

        // check for duplicate names
        let names: string[] = [];
        for (let d of this.fields) {
            if (d.name && names.indexOf(d.name) !== -1) {
                issues.push(new ValidationIssue("Field " + d.name + " is duplicated"));
            } else {
                names.push(d.name);
            }
        }

        return issues;
    }

    private compareObjectToStructure(value: AppGlueAny, issues: string[]): string[] {
        if (!this.fields || this.fields.length === 0) return issues;

        for (let field of this.fields) {
            try {
                let val = Reflect.get(value, field.name);
                if (val === undefined) {
                    issues.push("field is missing: " + field.name);
                    continue;
                }
                let issue = field.verifyValueType(val);

                if (issue) {
                    issues.push(`${field.name}: [${issue.issue}]`);
                }
            } catch (e) {
                issues.push(e.message);
            }
        }

        return issues;
    }
}
